import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout, { type StepDef } from '@/components/feature/AppLayout';
import {
  saveWizardSnapshot,
  loadWizardSnapshot,
  clearWizardSnapshot,
  dataUrlToFile,
} from '@/lib/wizardSnapshot';
import StepCategory, { type PostType } from './components/StepCategory';
import StepPlatform from './components/StepPlatform';
import StepUpload from './components/StepUpload';
import StepDetails from './components/StepDetails';
import StepBackground from './components/StepBackground';
import StepQuality from './components/StepQuality';
import StepPreview from './components/StepPreview';
import StepText from './components/StepText';
import StepSocialDesign, { type SocialDesignId } from './components/StepSocialDesign';
import StepSocialFinal from './components/StepSocialFinal';
import StepFashionModel from './components/StepFashionModel';
import type { ShapeOverlay, SuggestedFrame, TextOverlay } from './lib/composeRender';
import { socialPlatforms, productShotFormats, socialPostFormats, fashionModels } from '@/mocks/app-data';
import { sceneNames } from '@/mocks/demo';
import { type Quality, type Provider, type SocialPostSlots, suggestCopy } from '@/lib/api';
import { useCredits } from '@/hooks/useCredits';

// Per-route step structures:
//   Product Shot — DEFAULT_STEPS (8): all stages including Platform picker
//   Carousel     — CAROUSEL_STEPS (7): same as default minus Platform (all
//                                       carousel platforms are 1080×1080)
//   Fashion      — FASHION_STEPS (7): no Platform (1:1 locked to model),
//                                       Model picker replaces Background
//   Social Post  — SOCIAL_POST_STEPS (6): no Bg/Quality/Preview/Text —
//                                       collapsed into Design + Final
const DEFAULT_STEPS: StepDef[] = [
  { num: 1, label: 'Type',       icon: 'ri-stack-line' },
  { num: 2, label: 'Platform',   icon: 'ri-smartphone-line' },
  { num: 3, label: 'Product',    icon: 'ri-upload-cloud-line' },
  { num: 4, label: 'Details',    icon: 'ri-sparkling-2-line' },
  { num: 5, label: 'Background', icon: 'ri-palette-line' },
  { num: 6, label: 'Quality',    icon: 'ri-vip-crown-line' },
  { num: 7, label: 'Generation',    icon: 'ri-image-line' },
  { num: 8, label: 'Add Text',   icon: 'ri-text' },
];

const SOCIAL_POST_STEPS: StepDef[] = [
  { num: 1, label: 'Type',     icon: 'ri-stack-line' },
  { num: 2, label: 'Platform', icon: 'ri-smartphone-line' },
  { num: 3, label: 'Product',  icon: 'ri-upload-cloud-line' },
  { num: 4, label: 'Details',  icon: 'ri-sparkling-2-line' },
  { num: 5, label: 'Design',   icon: 'ri-layout-grid-line' },
  { num: 6, label: 'Final',    icon: 'ri-magic-line' },
];

// Fashion route — 7 steps. No Platform step (output is fixed 1:1 1024×1024
// to match the model template) and no Garment Type step (auto-detected
// from the upload via analyze-product Vision). Model picker replaces
// Background.
const FASHION_STEPS: StepDef[] = [
  { num: 1, label: 'Type',     icon: 'ri-stack-line' },
  { num: 2, label: 'Garment',  icon: 'ri-upload-cloud-line' },
  { num: 3, label: 'Details',  icon: 'ri-sparkling-2-line' },
  { num: 4, label: 'Model',    icon: 'ri-user-line' },
  { num: 5, label: 'Quality',  icon: 'ri-vip-crown-line' },
  { num: 6, label: 'Generation',  icon: 'ri-image-line' },
  { num: 7, label: 'Add Text', icon: 'ri-text' },
];

// Carousel route — 7 steps. No Platform step because all 3 carousel
// platforms (Instagram / Facebook / LinkedIn) are 1080×1080, so the choice
// would be a no-op. Platform auto-sets to CAROUSEL_DEFAULT_PLATFORM in
// handlePostTypeSelect when user picks this category.
const CAROUSEL_STEPS: StepDef[] = [
  { num: 1, label: 'Type',       icon: 'ri-stack-line' },
  { num: 2, label: 'Product',    icon: 'ri-upload-cloud-line' },
  { num: 3, label: 'Details',    icon: 'ri-sparkling-2-line' },
  { num: 4, label: 'Background', icon: 'ri-palette-line' },
  { num: 5, label: 'Quality',    icon: 'ri-vip-crown-line' },
  { num: 6, label: 'Generation',    icon: 'ri-image-line' },
  { num: 7, label: 'Add Text',   icon: 'ri-text' },
];

// Default platform id used when the user picks Carousel category. The other
// two carousel platforms (facebook-carousel / linkedin-carousel) are also
// 1080×1080 so this is purely a routing/display label — generation output
// is identical regardless. If we ever add carousel platforms with different
// dimensions, surface a platform picker again.
const CAROUSEL_DEFAULT_PLATFORM = 'instagram-carousel';

// Video route was here — removed May 26 with the rest of the video code.
// See VIDEO-RESEARCH.md for the full history (6-step flow, StepVideoGeneration
// component, etc.) and the suggested approach on resume.

function stepsForRoute(postType: PostType | null): StepDef[] {
  if (postType === 'single') return SOCIAL_POST_STEPS;
  if (postType === 'fashion') return FASHION_STEPS;
  if (postType === 'carousel') return CAROUSEL_STEPS;
  return DEFAULT_STEPS;
}

export default function AppPage() {
  const navigate = useNavigate();
  const { balance, planTier, refresh: refreshCredits } = useCredits();

  // Restore wizard state from sessionStorage (if any). Survives the
  // Stripe Checkout round-trip — free users redirected to pay land back
  // here with all prior steps intact. Snapshot has a 30-min TTL; older
  // saves are auto-discarded by loadWizardSnapshot.
  //
  // We read this once at mount via useRef + lazy init so each useState
  // call below can seed from the same snapshot without re-reading
  // sessionStorage (which would also force a JSON.parse per call).
  const restoredRef = useRef(loadWizardSnapshot());
  const restored = restoredRef.current;

  const [step, setStep] = useState(restored?.step ?? 1);
  // Step 1 — content category (Single / Carousel / Product Shot). Captured
  // up-front so we can later filter Step 2's platforms by intent, but
  // currently doesn't affect downstream behavior — just recorded in state.
  const [postType, setPostType] = useState<PostType | null>(restored?.postType ?? null);
  const [platform, setPlatform] = useState<string | null>(restored?.platform ?? null);
  const [productImages, setProductImages] = useState<string[]>(restored?.productImages ?? []);
  // Snapshot saves data URLs (productImages) + the original Files'
  // names. We reconstruct File objects with the SAME .name so
  // StepPreview's generation cache key (which includes productFile.name)
  // stays stable — otherwise a returning user gets re-charged for the
  // same /api/generate call.
  const [productFiles, setProductFiles] = useState<File[]>(() => {
    if (!restored?.productImages?.length) return [];
    const names = restored.productFileNames ?? [];
    return restored.productImages
      .map((url, i) => dataUrlToFile(url, names[i] ?? `restored-${i}.jpg`))
      .filter((f): f is File => f !== null);
  });
  const [productName, setProductName] = useState(restored?.productName ?? '');
  const [productDescription, setProductDescription] = useState(restored?.productDescription ?? '');
  const [costSpent, setCostSpent] = useState(0);
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>(restored?.textOverlays ?? []);
  const [shapeOverlays, setShapeOverlays] = useState<ShapeOverlay[]>(restored?.shapeOverlays ?? []);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(restored?.selectedPresetId ?? null);
  const [sceneId, setSceneId] = useState<string | null>(restored?.sceneId ?? null);
  // Quality of generated images. Free trial users are gated to 'low' by
  // StepQuality's UI + backend enforcement; non-free can pick any tier.
  const [quality, setQuality] = useState<Quality>(restored?.quality ?? 'low');
  // Experimental: image-gen provider toggle (Step 5). 'gemini' is enabled
  // only for 1:1 aspect ratios. Default stays on 'openai' so the production
  // path is unchanged for anyone who doesn't touch the toggle.
  const [provider, setProvider] = useState<Provider>(restored?.provider ?? 'openai');
  // Social Post route only — picked on Step 5 of the social-post flow.
  const [socialDesignId, setSocialDesignId] = useState<SocialDesignId | null>(restored?.socialDesignId ?? null);
  // Fashion route only — picked on Step 4 (Model). One of fashionModels[*].id.
  const [fashionModelId, setFashionModelId] = useState<string | null>(restored?.fashionModelId ?? null);
  // Fashion route only — auto-detected on Step 3 (Details) by analyze-product
  // Vision. Selects which mask region the backend uses on /generate. 'none'
  // means the upload isn't a clothing item; the UI surfaces a warning so the
  // user can re-upload before burning credits on a misclassified swap.
  const [garmentType, setGarmentType] = useState<'top' | 'bottoms' | 'shoes' | 'none' | null>(restored?.garmentType ?? null);
  // Fashion route only — restricted-content flag from analyze-product.
  // Anything other than 'none' blocks the Step 3 → 4 transition so we
  // don't hit OpenAI's content policy on swimwear / lingerie / etc.
  const [garmentRestriction, setGarmentRestriction] = useState<
    'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none' | null
  >(restored?.garmentRestriction ?? null);
  // Product Shot route only — human-model flag. Drives a soft warning
  // on Step 4 (Details) suggesting the Fashion route. Non-blocking.
  const [hasHumanModel, setHasHumanModel] = useState<boolean | null>(restored?.hasHumanModel ?? null);
  // Product Shot route — auto-detected on Step 3 by analyze-product. The
  // category (e.g. 'skincare', 'fragrance') + vibe tags (e.g. ['feminine',
  // 'luxury', 'soft-light']) feed StepBackground's "Recommended for you"
  // tab. Stays null until analyze-product returns; the rec tab is hidden
  // in that case so the picker degrades to aesthetic-only browsing.
  const [productCategory, setProductCategory] = useState<string | null>(restored?.productCategory ?? null);
  const [productTags, setProductTags] = useState<string[]>(restored?.productTags ?? []);
  // Social Post route only — 12-slot copy from /api/suggest-copy?format=social-post-12-slot,
  // populated on Step 6 mount (or set manually via the Tweaks Panel).
  const [socialSlots, setSocialSlots] = useState<SocialPostSlots | null>(restored?.socialSlots ?? null);
  // Restore generation outputs from snapshot so a free-tier user who
  // upgraded mid-flow and came back lands on Step 7 (Generation) seeing
  // the SAME images they already paid credits for. StepPreview's cache
  // key check (uses lastGenKey) is what prevents the duplicate
  // /api/generate call — restoring lastGenKey alongside the images is
  // what makes that work.
  const [generatedImages, setGeneratedImages] = useState<string[]>(restored?.generatedImages ?? []);
  const [suggestedFrames, setSuggestedFrames] = useState<SuggestedFrame[]>(restored?.suggestedFrames ?? []);
  const [lastGenKey, setLastGenKey] = useState<string | null>(restored?.lastGenKey ?? null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [showNewPostConfirm, setShowNewPostConfirm] = useState(false);

  // Persist wizard state to sessionStorage on every change. This is what
  // lets free users round-trip through Stripe Checkout without losing
  // their progress (#9). Debounced to one-write-per-tick via the deps
  // array; sessionStorage writes are cheap so we don't bother with
  // setTimeout debouncing. Skipped at step 1 with no postType picked —
  // that's the fresh-start state, no point persisting it.
  useEffect(() => {
    const isFreshStart = step === 1 && !postType;
    if (isFreshStart) {
      clearWizardSnapshot();
      return;
    }
    saveWizardSnapshot({
      step,
      postType,
      platform,
      productImages,
      // Persist Files' .name values alongside the data URLs so StepPreview's
      // cache key remains identical across the restore cycle.
      productFileNames: productFiles.map((f) => f.name),
      productName,
      productDescription,
      sceneId,
      quality,
      provider,
      socialDesignId,
      fashionModelId,
      garmentType,
      garmentRestriction,
      hasHumanModel,
      productCategory,
      productTags,
      socialSlots,
      // Generation outputs — preserved so a returning user doesn't pay
      // for the same /api/generate twice (bug observed: user upgrades
      // mid-flow, lands back on Step 7, image re-renders losing 1 credit).
      generatedImages,
      suggestedFrames,
      lastGenKey,
      textOverlays,
      shapeOverlays,
      selectedPresetId,
    });
  }, [
    step, postType, platform, productImages, productFiles, productName,
    productDescription, sceneId, quality, provider, socialDesignId,
    fashionModelId, garmentType, garmentRestriction, hasHumanModel,
    productCategory, productTags, socialSlots,
    generatedImages, suggestedFrames, lastGenKey, textOverlays,
    shapeOverlays, selectedPresetId,
  ]);

  const maxImages = 1;
  // Combined lookup — Step 2 lets the user pick from either socialPlatforms
  // (single/carousel categories) or productShotFormats (product-shot
  // category). Both data sets share the same shape so downstream code
  // (aspect ratio, dimensions, name, etc.) reads from a single object.
  const platformObj =
    socialPlatforms.find((p) => p.id === platform) ??
    productShotFormats.find((p) => p.id === platform) ??
    socialPostFormats.find((p) => p.id === platform);
  const platformAspectRatio = platformObj?.aspectRatio || '1:1';

  // Parse the platform's `dimensions` string ("1080 x 1080" / "1200 x 1200")
  // into numeric width/height. Used only by the Social Post route's final
  // step to scale the html-to-image export to the platform's native PNG
  // resolution. Falls back to 1080×1080 if the dimensions string is
  // missing or malformed.
  const dimsParts = (platformObj?.dimensions ?? '1080 x 1080')
    .split('x')
    .map((s) => parseInt(s.trim(), 10));
  const exportWidth = Number.isFinite(dimsParts[0]) ? dimsParts[0] : 1080;
  const exportHeight = Number.isFinite(dimsParts[1]) ? dimsParts[1] : 1080;
  // Carousel = any platform with the 'Multi' badge (instagram/facebook/
  // linkedin carousel). Previously hardcoded to 'instagram-carousel' which
  // misclassified the other two. The badge convention keeps this generic
  // for any future carousel platforms we add.
  const isCarousel = (platformObj as { badge?: string } | undefined)?.badge === 'Multi';

  // Friendly labels for the new Category step's summary in the sidebar.
  // Mirrors the labels shown in StepCategory.tsx — keep them in sync.
  const POST_TYPE_LABEL: Record<PostType, string> = {
    'single':       'Social Post',
    'carousel':     'Carousel',
    'product-shot': 'Product Shot',
    'fashion':      'Fashion',
  };

  // Friendly labels for the social-post design summary.
  const SOCIAL_DESIGN_LABEL: Record<SocialDesignId, string> = {
    'editorial': 'Editorial Cream',
    'blush':     'Soft Blush',
    'botanical': 'Botanical Sage',
    'mono':      'Mono Editorial',
  };

  const fashionModelObj = fashionModels.find((m) => m.id === fashionModelId);

  // Per-step summary strings surfaced in the sidebar's stepper. Order matches
  // the active route's `steps` array. AppLayout substitutes summary for the
  // generic label when present.
  let stepSummaries: (string | null)[];
  if (postType === 'single') {
    stepSummaries = [
      postType ? POST_TYPE_LABEL[postType] : null,                  // 1 Type
      platformObj?.name ?? null,                                    // 2 Platform
      productFiles[0] ? 'Uploaded image' : null,                    // 3 Upload
      productName.trim() || null,                                   // 4 Details
      socialDesignId ? SOCIAL_DESIGN_LABEL[socialDesignId] : null,  // 5 Design
      null,                                                         // 6 Final — terminal
    ];
  } else if (postType === 'fashion') {
    stepSummaries = [
      postType ? POST_TYPE_LABEL[postType] : null,                  // 1 Type
      productFiles[0] ? 'Garment uploaded' : null,                  // 2 Garment
      productName.trim() || null,                                   // 3 Details
      fashionModelObj?.name ?? null,                                // 4 Model
      quality ? quality.charAt(0).toUpperCase() + quality.slice(1) : null, // 5 Quality
      generatedImages.length > 0
        ? `${generatedImages.length} image${generatedImages.length === 1 ? '' : 's'}`
        : null,                                                     // 6 Preview
      null,                                                         // 7 Add Text
    ];
  } else if (postType === 'carousel') {
    // Carousel route — 7 steps, no Platform.
    stepSummaries = [
      postType ? POST_TYPE_LABEL[postType] : null,                  // 1 Type
      productFiles[0] ? 'Uploaded image' : null,                    // 2 Upload
      productName.trim() || null,                                   // 3 Details
      sceneId ? (sceneNames[sceneId] || sceneId) : null,            // 4 Background
      quality ? quality.charAt(0).toUpperCase() + quality.slice(1) : null, // 5 Quality
      generatedImages.length > 0
        ? `${generatedImages.length} image${generatedImages.length === 1 ? '' : 's'}`
        : null,                                                     // 6 Preview
      null,                                                         // 7 Add Text
    ];
  } else {
    stepSummaries = [
      postType ? POST_TYPE_LABEL[postType] : null,                  // 1 Type
      platformObj?.name ?? null,                                    // 2 Platform
      productFiles[0] ? 'Uploaded image' : null,                    // 3 Upload
      productName.trim() || null,                                   // 4 Details
      sceneId ? (sceneNames[sceneId] || sceneId) : null,            // 5 Background
      quality ? quality.charAt(0).toUpperCase() + quality.slice(1) : null, // 6 Quality
      generatedImages.length > 0
        ? `${generatedImages.length} image${generatedImages.length === 1 ? '' : 's'}`
        : null,                                                     // 7 Preview
      null,                                                         // 8 Add Text
    ];
  }

  const isSocialRoute = postType === 'single';
  const isFashionRoute = postType === 'fashion';
  const isCarouselRoute = postType === 'carousel';
  const steps = stepsForRoute(postType);

  const canProceed = useCallback(() => {
    // Social Post route — 6 steps, different gate on Step 5 (design instead
    // of scene/background).
    if (postType === 'single') {
      switch (step) {
        case 1: return !!postType;
        case 2: return !!platform;
        case 3: return productImages.length >= 1;
        case 4: return productName.trim().length > 0;
        case 5: return !!socialDesignId;
        default: return false;          // Step 6 is terminal (no "next")
      }
    }
    // Fashion route — 7 steps. No Platform step; Step 4 is Model picker.
    if (postType === 'fashion') {
      switch (step) {
        case 1: return !!postType;
        case 2: return productImages.length >= 1;       // Garment upload
        case 3:
          // Details gate must check the name, the auto-detected garment
          // classification, AND that the item isn't flagged as restricted
          // content (swimwear / lingerie / underwear / sheer). The
          // restriction gate prevents users from burning credits hitting
          // OpenAI's content policy at /generate time. StepDetails
          // surfaces the matching inline warning that explains which
          // gate failed.
          return (
            productName.trim().length > 0 &&
            garmentType !== null &&
            garmentType !== 'none' &&
            (garmentRestriction === null || garmentRestriction === 'none')
          );
        case 4: return !!fashionModelId;                // Model
        case 5: return !!quality;                       // Quality
        default: return false;                          // Preview + Add Text self-wrap
      }
    }
    // Carousel route — 7 steps. No Platform step (auto-set to default
    // carousel platform on category select). More permissive than Product
    // Shot: no hasHumanModel block since carousel users may want lifestyle
    // imagery with people.
    if (isCarouselRoute) {
      switch (step) {
        case 1: return !!postType;
        case 2: return productImages.length >= 1;             // Upload
        case 3: return productName.trim().length > 0;         // Details
        case 4: return !!sceneId;                             // Background
        case 5: return !!quality;                             // Quality
        default: return false;                                // Preview + Add Text self-wrap
      }
    }
    // Default 8-step route (Product Shot).
    switch (step) {
      case 1: return !!postType;
      case 2: return !!platform;
      case 3: return productImages.length >= 1;
      case 4:
        // Product Shot is for accessories / standalone product photos
        // (shoes, watches, jewelry, beauty, fragrance, candles). Block
        // Continue when the upload contains a person — those belong in
        // the Fashion route which has the model-template + garment-swap
        // pipeline.
        return (
          productName.trim().length > 0 &&
          !(postType === 'product-shot' && hasHumanModel === true)
        );
      case 5: return !!sceneId;
      case 6: return !!quality;  // always true, quality defaults to 'low'
      default: return false;
    }
  }, [step, postType, platform, productImages.length, productName, sceneId, quality, socialDesignId, fashionModelId, garmentType, garmentRestriction, hasHumanModel, isCarouselRoute]);

  const hasProgress = !!postType || !!platform || productImages.length > 0 || textOverlays.length > 0 || !!sceneId || !!socialDesignId || !!fashionModelId;

  const handleNext = () => {
    if (step < steps.length && canProceed()) {
      setStep(step + 1);
    }
  };

  const handleCostAdd = useCallback((delta: number) => {
    setCostSpent((prev) => prev + delta);
  }, []);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      return;
    }
    // On step 1 (Category), Back exits the wizard. Routes through the
    // existing "exit to gallery" confirmation modal so users with progress
    // get a chance to cancel — but step 1 has minimal state so this is
    // usually a clean exit. Defining this explicitly so the Back button
    // works on every step (not just 2-8), matching the user expectation
    // of "back always does something".
    handleExitGallery();
  };

  const handleReset = () => {
    setStep(1);
    setPostType(null);
    setPlatform(null);
    setProductImages([]);
    setProductFiles([]);
    setProductName('');
    setProductDescription('');
    setCostSpent(0);
    setTextOverlays([]);
    setShapeOverlays([]);
    setSelectedPresetId(null);
    setSceneId(null);
    setQuality('low');
    setProvider('openai');
    setSocialDesignId(null);
    setSocialSlots(null);
    setFashionModelId(null);
    setGarmentType(null);
    setGarmentRestriction(null);
    setHasHumanModel(null);
    setProductCategory(null);
    setProductTags([]);
    setGeneratedImages([]);
    setSuggestedFrames([]);
    setLastGenKey(null);
  };

  const handleGenerationComplete = useCallback((images: string[], frames: SuggestedFrame[], key: string) => {
    setGeneratedImages(images);
    setSuggestedFrames(frames);
    setLastGenKey(key);
  }, []);

  // Called from Step 6 when the user hits Regenerate. Returns the fresh frames
  // synchronously so the caller can re-apply the current preset against the
  // new copy without waiting for the parent state update to flush.
  const handleRegenerateCopy = useCallback(async (): Promise<SuggestedFrame[] | null> => {
    const file = productFiles[0];
    if (!file) return null;
    try {
      const result = await suggestCopy({
        productFile: file,
        productName,
        productDescription,
        frameCount: isCarousel ? 4 : 1,
      });
      setSuggestedFrames(result.frames);
      handleCostAdd(result._cost ?? 0);
      return result.frames;
    } catch (err) {
      console.error('Regenerate copy failed:', err);
      return null;
    }
  }, [productFiles, productName, productDescription, isCarousel, handleCostAdd]);

  const handleStepClick = (targetStep: number) => {
    if (targetStep <= step || targetStep === step + 1) {
      setStep(targetStep);
    }
  };

  const handleExitGallery = () => {
    if (hasProgress) {
      setShowExitConfirm(true);
    } else {
      navigate('/gallery');
    }
  };

  const confirmExit = () => {
    setShowExitConfirm(false);
    navigate('/gallery');
  };

  const cancelExit = () => {
    setShowExitConfirm(false);
  };

  // Sidebar New Creative — confirms only if there's unsaved progress, otherwise
  // resets immediately so the click feels instant from a clean state.
  const handleNewPost = () => {
    if (hasProgress) {
      setShowNewPostConfirm(true);
    } else {
      handleReset();
    }
  };

  const confirmNewPost = () => {
    setShowNewPostConfirm(false);
    handleReset();
  };

  const cancelNewPost = () => {
    setShowNewPostConfirm(false);
  };

  const handlePlatformSelect = useCallback((id: string) => {
    setPlatform(id);
  }, []);

  const handleUpload = useCallback((images: string[], files: File[]) => {
    setProductImages(images);
    setProductFiles(files);
    // Clear ALL downstream state derived from the previous product so a
    // fresh upload (manual replace OR URL re-scrape) doesn't leak stale
    // data into Details / Preview / Social compose. The URL-scrape path
    // re-populates name + description immediately via onScrapeSuccess,
    // so wiping them here doesn't cause a flicker. Manual replace
    // depends on this wipe to trigger StepDetails' analyze-product
    // (its skip-guard short-circuits when productName is truthy).
    setProductName('');
    setProductDescription('');
    setGarmentType(null);
    setGarmentRestriction(null);
    setHasHumanModel(null);
    setProductCategory(null);
    setProductTags([]);
    // Wipe generated outputs — old AI composites are meaningless for the
    // new product. The user expects Step 6 (Preview) to regenerate, not
    // show the previous post.
    setGeneratedImages([]);
    setSuggestedFrames([]);
    setLastGenKey(null);
    setSocialSlots(null);
    setTextOverlays([]);
    setShapeOverlays([]);
    setSelectedPresetId(null);
  }, []);

  const handleSceneSelect = useCallback((id: string) => {
    setSceneId(id);
  }, []);

  // Category switch — wipe the user-data state from the prior route so
  // Fashion uploads don't leak into Product Shot composing (and vice
  // versa). First-time selection (postType still null) skips the wipe
  // since there's nothing stale to clear. Keep costSpent + quality +
  // provider since those are session-level, not route-specific.
  const handlePostTypeSelect = useCallback((newType: PostType) => {
    if (postType !== null && newType !== postType) {
      setPlatform(null);
      setProductImages([]);
      setProductFiles([]);
      setProductName('');
      setProductDescription('');
      setGarmentType(null);
      setGarmentRestriction(null);
      setProductCategory(null);
      setProductTags([]);
      setSceneId(null);
      setSocialDesignId(null);
      setSocialSlots(null);
      setFashionModelId(null);
      setGeneratedImages([]);
      setSuggestedFrames([]);
      setLastGenKey(null);
      setTextOverlays([]);
      setShapeOverlays([]);
      setSelectedPresetId(null);
    }
    setPostType(newType);
    // Carousel route skips the Platform step (all 3 carousel platforms are
    // 1080×1080 — see CAROUSEL_STEPS). Auto-pick the default so downstream
    // code that reads platformObj / platformAspectRatio / dimensions works
    // without the user ever seeing a platform picker.
    if (newType === 'carousel') {
      setPlatform(CAROUSEL_DEFAULT_PLATFORM);
    }
  }, [postType]);

  const renderStepContent = () => {
    // Fashion route has no Platform step, so step numbers don't line up with
    // the default-route switch below. Branch first and map step → component
    // explicitly. Aspect ratio is hardcoded to 1:1 because the model
    // template (base.png) is 1024×1024.
    if (isFashionRoute) {
      switch (step) {
        case 1:
          return <StepCategory selected={postType} onSelect={handlePostTypeSelect} />;
        case 2:
          return (
            <StepUpload
              uploadedImages={productImages}
              maxImages={maxImages}
              onUpload={handleUpload}
              platformId="instagram"
              onScrapeSuccess={(name, description) => {
                setProductName(name);
                setProductDescription(description);
              }}
            />
          );
        case 3:
          return (
            <StepDetails
              productFile={productFiles[0] || null}
              productImage={productImages[0] || ''}
              productName={productName}
              productDescription={productDescription}
              onProductNameChange={setProductName}
              onProductDescriptionChange={setProductDescription}
              onGarmentTypeDetected={setGarmentType}
              onGarmentRestrictionDetected={setGarmentRestriction}
              requireGarment
              garmentType={garmentType}
              garmentRestriction={garmentRestriction}
              onCostAdd={handleCostAdd}
            />
          );
        case 4:
          return <StepFashionModel selected={fashionModelId} onSelect={setFashionModelId} />;
        case 5:
          return (
            <StepQuality
              selected={quality}
              onSelect={setQuality}
              isCarousel={false}
              planTier={planTier}
              balance={balance}
              provider={provider}
              onProviderChange={setProvider}
              aspectRatio="1:1"
            />
          );
        case 6:
          return (
            <StepPreview
              productFile={productFiles[0] || null}
              platformId="instagram"
              platformAspectRatio="1:1"
              templateId={fashionModelId || ''}
              productName={productName}
              productDescription={productDescription}
              quality={quality}
              onQualityChange={setQuality}
              provider={provider}
              // canProceed() blocks Step 3 → Step 4 if garmentType is null
              // or 'none', so by Step 6 it's always a real region. Narrow
              // here for the type-checker; fall back to 'top' only as a
              // belt-and-suspenders default if state somehow leaks through.
              garmentType={
                garmentType && garmentType !== 'none' ? garmentType : 'top'
              }
              isCarousel={false}
              planTier={planTier}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              lastGenKey={lastGenKey}
              onGenerationComplete={handleGenerationComplete}
              onCostAdd={handleCostAdd}
              onCreditsChanged={refreshCredits}
              onBack={handleBack}
              onContinue={() => setStep(7)}
            />
          );
        case 7:
          return (
            <StepText
              platformAspectRatio="1:1"
              templateId={fashionModelId || ''}
              isCarousel={false}
              overlays={textOverlays}
              onOverlaysChange={setTextOverlays}
              shapeOverlays={shapeOverlays}
              onShapeOverlaysChange={setShapeOverlays}
              selectedPresetId={selectedPresetId}
              onPresetSelect={setSelectedPresetId}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              onRegenerateCopy={handleRegenerateCopy}
              onBack={handleBack}
            />
          );
        default:
          return null;
      }
    }

    // Carousel route — 7 steps, no Platform (auto-set to instagram-carousel
    // by handlePostTypeSelect since all 3 carousel platforms are 1080×1080).
    // Step number map: 1=Category, 2=Upload, 3=Details, 4=Background,
    // 5=Quality, 6=Preview, 7=Add Text.
    if (isCarouselRoute) {
      switch (step) {
        case 1:
          return <StepCategory selected={postType} onSelect={handlePostTypeSelect} />;
        case 2:
          return (
            <StepUpload
              uploadedImages={productImages}
              maxImages={maxImages}
              onUpload={handleUpload}
              platformId={platform || 'instagram-carousel'}
              onScrapeSuccess={(name, description, insights) => {
                setProductName(name);
                setProductDescription(description);
                if (insights?.productCategory) setProductCategory(insights.productCategory);
                if (insights?.tags && insights.tags.length > 0) setProductTags(insights.tags);
              }}
            />
          );
        case 3:
          return (
            <StepDetails
              productFile={productFiles[0] || null}
              productImage={productImages[0] || ''}
              productName={productName}
              productDescription={productDescription}
              onProductNameChange={setProductName}
              onProductDescriptionChange={setProductDescription}
              onProductInsightsDetected={({ category, tags }) => {
                if (category) setProductCategory(category);
                if (tags) setProductTags(tags);
              }}
              // Carousel is the permissive route — no human-model warn (unlike
              // Product Shot which hard-blocks people).
              onHumanModelDetected={setHasHumanModel}
              hasHumanModel={hasHumanModel}
              onCostAdd={handleCostAdd}
            />
          );
        case 4:
          return (
            <StepBackground
              selected={sceneId}
              onSelect={handleSceneSelect}
              productCategory={productCategory ?? undefined}
              productTags={productTags}
            />
          );
        case 5:
          return (
            <StepQuality
              selected={quality}
              onSelect={setQuality}
              isCarousel
              planTier={planTier}
              balance={balance}
              provider={provider}
              onProviderChange={setProvider}
              aspectRatio={platformAspectRatio}
            />
          );
        case 6:
          return (
            <StepPreview
              productFile={productFiles[0] || null}
              platformId={platform || 'instagram-carousel'}
              platformAspectRatio={platformAspectRatio}
              templateId={sceneId || ''}
              productName={productName}
              productDescription={productDescription}
              quality={quality}
              onQualityChange={setQuality}
              provider={provider}
              isCarousel
              planTier={planTier}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              lastGenKey={lastGenKey}
              onGenerationComplete={handleGenerationComplete}
              onCostAdd={handleCostAdd}
              onCreditsChanged={refreshCredits}
              onBack={handleBack}
              onContinue={() => setStep(7)}
            />
          );
        case 7:
          return (
            <StepText
              platformAspectRatio={platformAspectRatio}
              templateId={sceneId || ''}
              isCarousel
              overlays={textOverlays}
              onOverlaysChange={setTextOverlays}
              shapeOverlays={shapeOverlays}
              onShapeOverlaysChange={setShapeOverlays}
              selectedPresetId={selectedPresetId}
              onPresetSelect={setSelectedPresetId}
              generatedImages={generatedImages}
              suggestedFrames={suggestedFrames}
              onRegenerateCopy={handleRegenerateCopy}
              onBack={handleBack}
            />
          );
        default:
          return null;
      }
    }

    // Video route was here (6 steps, StepVideoGeneration at Step 6) —
    // removed May 26 with the rest of the video code. See VIDEO-RESEARCH.md.

    switch (step) {
      case 1:
        return (
          <StepCategory
            selected={postType}
            onSelect={handlePostTypeSelect}
          />
        );
      case 2:
        return (
          <StepPlatform
            selected={platform}
            onSelect={handlePlatformSelect}
            postType={postType}
          />
        );
      case 3:
        return (
          <StepUpload
            uploadedImages={productImages}
            maxImages={maxImages}
            onUpload={handleUpload}
            platformId={platform || 'instagram'}
            onScrapeSuccess={(name, description, insights) => {
              // URL-scrape path: backend gave us name + description (free
              // from HTML parsing) PLUS productCategory + tags (cheap
              // text-only LLM classification, ~$0.0001). Pre-fill all of
              // it so Step 4 (Details) can skip the more expensive vision
              // analyze-product call, AND Step 5 (Background) has
              // insights ready for "Recommended for you" without any
              // extra work.
              setProductName(name);
              setProductDescription(description);
              if (insights?.productCategory) setProductCategory(insights.productCategory);
              if (insights?.tags && insights.tags.length > 0) setProductTags(insights.tags);
            }}
          />
        );
      case 4:
        return (
          <StepDetails
            productFile={productFiles[0] || null}
            productImage={productImages[0] || ''}
            productName={productName}
            productDescription={productDescription}
            onProductNameChange={setProductName}
            onProductDescriptionChange={setProductDescription}
            // Captures analyze-product's productCategory + tags so the
            // StepBackground "Recommended for you" tab can rank scenes.
            onProductInsightsDetected={({ category, tags }) => {
              if (category) setProductCategory(category);
              if (tags) setProductTags(tags);
            }}
            // Soft-warn Product Shot users when they upload a model
            // photo — those belong in the Fashion route. Carousel and
            // Social Post don't get the warning (more permissive flows).
            onHumanModelDetected={setHasHumanModel}
            warnHumanModel={postType === 'product-shot'}
            hasHumanModel={hasHumanModel}
            onCostAdd={handleCostAdd}
          />
        );
      case 5:
        // Social Post route diverges here: instead of an image-generation
        // template (StepBackground), the user picks one of the four designed
        // post layouts (Editorial / Blush / Botanical / Mono).
        if (isSocialRoute) {
          return (
            <StepSocialDesign
              selected={socialDesignId}
              onSelect={setSocialDesignId}
            />
          );
        }
        return (
          <StepBackground
            selected={sceneId}
            onSelect={handleSceneSelect}
            productCategory={productCategory ?? undefined}
            productTags={productTags}
            // Scene / Pattern tab toggle — Product Shot route only.
            // Pattern selections set sceneId to `pattern-<id>`; backend
            // dispatches on that prefix (mirrors `social-bg-` pattern).
            showPatternTab={postType === 'product-shot'}
          />
        );
      case 6:
        // Social Post route's terminal step — render the chosen design,
        // auto-fire the 12-slot suggest-copy call AND the social-bg
        // product-compose call (gpt-image-1 places the product onto the
        // design's solid-color slot bg), expose a tweaks panel, and let
        // the user download / save the rendered PNG.
        if (isSocialRoute) {
          return (
            <StepSocialFinal
              productFile={productFiles[0] || null}
              productImageUrl={productImages[0] || ''}
              productName={productName}
              productDescription={productDescription}
              designId={socialDesignId!}
              slots={socialSlots}
              onSlotsChange={setSocialSlots}
              exportWidth={exportWidth}
              exportHeight={exportHeight}
              onCostAdd={handleCostAdd}
              onCreditsChanged={refreshCredits}
              hasHumanModel={hasHumanModel}
              onBack={handleBack}
            />
          );
        }
        return (
          <StepQuality
            selected={quality}
            onSelect={setQuality}
            isCarousel={isCarousel}
            planTier={planTier}
            balance={balance}
            provider={provider}
            onProviderChange={setProvider}
            aspectRatio={platformAspectRatio}
          />
        );
      case 7:
        return (
          <StepPreview
            productFile={productFiles[0] || null}
            platformId={platform || 'instagram'}
            platformAspectRatio={platformAspectRatio}
            templateId={sceneId || ''}
            productName={productName}
            productDescription={productDescription}
            quality={quality}
            onQualityChange={setQuality}
            provider={provider}
            isCarousel={isCarousel}
            planTier={planTier}
            generatedImages={generatedImages}
            suggestedFrames={suggestedFrames}
            lastGenKey={lastGenKey}
            onGenerationComplete={handleGenerationComplete}
            onCostAdd={handleCostAdd}
            onCreditsChanged={refreshCredits}
            onBack={handleBack}
            onContinue={() => setStep(8)}
          />
        );
      case 8:
        return (
          <StepText
            platformAspectRatio={platformAspectRatio}
            templateId={sceneId || ''}
            isCarousel={isCarousel}
            overlays={textOverlays}
            onOverlaysChange={setTextOverlays}
            shapeOverlays={shapeOverlays}
            onShapeOverlaysChange={setShapeOverlays}
            selectedPresetId={selectedPresetId}
            onPresetSelect={setSelectedPresetId}
            generatedImages={generatedImages}
            suggestedFrames={suggestedFrames}
            onRegenerateCopy={handleRegenerateCopy}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <AppLayout
        currentStep={step}
        stepSummaries={stepSummaries}
        steps={steps}
        onStepClick={handleStepClick}
        onExitGallery={handleExitGallery}
        onNewPost={handleNewPost}
      >
        {/* Flex column: step content fills, buttons pinned at bottom.
            Steps that own their own internal layouts (StepPreview,
            StepText, StepSocialFinal) skip the white-panel wrapper. */}
        <div className="h-full flex flex-col">
          {/* The default 8-step route's StepPreview (step 7) + StepText
              (step 8) self-wrap. The social-post route's StepSocialFinal
              (step 6) also self-wraps. Everything else gets the unified
              panel wrapper. */}
          {(() => {
            const selfWraps = isSocialRoute
              ? step === 6                  // StepSocialFinal
              : isFashionRoute
                ? step >= 6                 // Fashion Preview (6) + Add Text (7)
                : isCarouselRoute
                  ? step >= 6               // Carousel Preview (6) + Add Text (7)
                  : step >= 7;              // Default Preview (7) + Add Text (8)
            return selfWraps ? (
              <div className="flex-1 min-h-0">
                {renderStepContent()}
              </div>
            ) : (
              <div className="flex-1 min-h-0 bg-surface-panel rounded-2xl border-2 border-white p-6 overflow-y-auto shadow-lg shadow-primary/15">
                {renderStepContent()}
              </div>
            );
          })()}

          {/* BOTTOM NAVIGATION — hidden on the self-wrapping steps which
              own their own footers (StepPreview / StepText / StepSocialFinal). */}
          {(() => {
            const showBottomNav = isSocialRoute
              ? step < 6                    // social route hides on Final (step 6)
              : isFashionRoute
                ? step < 6                  // fashion route hides on Preview (6) + Add Text (7)
                : isCarouselRoute
                  ? step < 6                // carousel route hides on Preview (6) + Add Text (7)
                  : step < 7;               // default route hides on Preview + Text (steps 7, 8)
            // "Continue" button label flips to "Generate Post" on the last
            // step before the AI generation kicks off. Last gating step:
            //   default (Product Shot): step 6 (Quality)
            //   fashion:                step 5 (Quality)
            //   carousel:               step 5 (Quality)
            const isGenerateStep =
              (!isSocialRoute && !isFashionRoute && !isCarouselRoute && step === 6) ||
              (isFashionRoute && step === 5) ||
              (isCarouselRoute && step === 5);
            return showBottomNav && (
              <div className="flex-shrink-0 mt-3 bg-surface-panel rounded-2xl border-2 border-white px-5 py-2.5 shadow-lg shadow-primary/15">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleBack}
                    className="inline-flex items-center gap-2.5 px-7 py-2.5 rounded-full text-base font-semibold transition-all cursor-pointer whitespace-nowrap text-dark hover:bg-surface"
                  >
                    <i className="ri-arrow-left-line text-lg" />
                    Back
                  </button>

                  <button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className={`inline-flex items-center gap-2.5 px-9 py-2.5 rounded-full text-base font-bold transition-all cursor-pointer whitespace-nowrap ${
                      canProceed()
                        ? 'bg-primary hover:bg-primary-hover text-white shadow-lg shadow-primary/15'
                        : 'bg-surface-dark text-dark-muted opacity-60 cursor-not-allowed'
                    }`}
                  >
                    {isGenerateStep ? (
                      <>
                        <i className="ri-magic-line text-lg" />
                        Generate Post
                      </>
                    ) : (
                      <>
                        Continue
                        <i className="ri-arrow-right-line text-lg" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </AppLayout>

      {/* New Creative Confirmation Modal */}
      {showNewPostConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-amber-500 text-xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-lg mb-2">
              Start a New Creative?
            </h3>
            <p className="text-dark-muted text-sm mb-2">
              You&apos;re about to start over from Step 1.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
              <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
                <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0" />
                Your current progress will be lost. Save to My Creatives first if you want to keep it.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelNewPost}
                className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
              >
                Keep Working
              </button>
              <button
                onClick={confirmNewPost}
                className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-md w-full shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center mb-4">
              <i className="ri-error-warning-line text-amber-500 text-xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-lg mb-2">
              Exit Post Creator?
            </h3>
            <p className="text-dark-muted text-sm mb-2">
              You are about to leave the Post Creator and return to My Creatives.
            </p>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-6">
              <p className="text-amber-800 text-sm font-medium flex items-start gap-2">
                <i className="ri-alert-line text-amber-500 mt-0.5 flex-shrink-0" />
                Your current progress will not be saved. You will need to start over if you return.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={cancelExit}
                className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
              >
                Stay & Continue
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer"
              >
                Exit to My Creatives
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}